import { useMemo } from 'react';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { HighnoteProductType, Role } from 'API';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { getRoleAccess } from 'app/auth/roleAccess';

export const useUserRole = () => {
  const { userRole, user } = useAppSelector(selectUserCompanies);
  const { integration } = useAppSelector(selectReviewOnboardSlice);

  const {
    // SEE-946: `additionalAdminRolesEnabled` is the per-paidol AppConfig
    // gate that controls ONLY the invite surface — i.e. whether the role
    // dropdown in AddUserForm / EditUserForm offers Secondary / Approval as
    // assignable options. It does NOT gate the capability checks below;
    // those depend purely on the user's stored role. Two reasons:
    //  1) Once a user is stored as SECONDARY_ADMINISTRATOR / APPROVAL_ADMINISTRATOR
    //     in DDB, they ARE that role. A paidol-level toggle shouldn't
    //     flip what an existing user can do — that would create a confusing
    //     "your access just changed because someone toggled a flag"
    //     experience and was the source of an admin-tab flash during
    //     context hydration (the flag value was briefly undefined and the
    //     old fail-open path promoted them to ADMINISTRATOR for one frame).
    //  2) Non-flag paidols can never get a user with these stored roles in
    //     the first place — the invite UI is gated by the flag, so
    //     `userRole === SECONDARY_ADMINISTRATOR` is impossible there. The
    //     "no regression" contract holds without any fail-open: those
    //     paidols only ever see ADMINISTRATOR / GROUP_ADMINISTRATOR /
    //     CARDHOLDER / MEMBER, which all behave exactly as before.
    'adminRoleFeatures.additionalAdminRolesEnabled': adminRolesFlagValue,
  } = useIsEnabled([{ featureName: 'adminRoleFeatures', flagName: 'additionalAdminRolesEnabled' }]);
  const additionalAdminRolesEnabled = adminRolesFlagValue === true;

  // Capability composites are derived directly from the stored role —
  // no flag indirection, no transient `undefined` window, no fail-open.
  const isAdministrator = Role.ADMINISTRATOR === userRole;
  const isSecondaryAdmin = Role.SECONDARY_ADMINISTRATOR === userRole;
  const isApprovalAdmin = Role.APPROVAL_ADMINISTRATOR === userRole;
  const isGroupAdmin = Role.GROUP_ADMINISTRATOR === userRole;
  const isCardholder = Role.CARDHOLDER === userRole;

  // Composite capabilities — single source of truth for role-gated UI.
  // Matches the SEE-946 permissions matrix on the ticket.
  //
  // SEE-1081 ([Whitaker]): the Secondary Admin gained transaction VISIBILITY,
  // the ability to MARK REVIEWED, and the matching review-notification
  // settings (they reconcile/export approved transactions to VISTA/COINS, so
  // they need to see, acknowledge, and be alerted on them). They explicitly do
  // NOT gain final approval or ERP post — those stay on
  // canApproveTransactionFinal / canApproveAndPostErp below.
  const canManageUsersAndCards = isAdministrator || isSecondaryAdmin;
  const canAccessSettings = isAdministrator || isSecondaryAdmin;
  const canAccessTransactions = isAdministrator || isSecondaryAdmin || isGroupAdmin || isApprovalAdmin;
  const canMarkTransactionReviewed = isAdministrator || isSecondaryAdmin || isGroupAdmin || isApprovalAdmin;
  const canApproveTransactionFinal = isAdministrator || isApprovalAdmin;
  const canViewTxnReviewNotifications =
    isAdministrator || isSecondaryAdmin || isGroupAdmin || isApprovalAdmin;

  // SEE-946 new composites (driven by the acceptance criteria expansion):
  //
  // canApproveAndPostErp — gates APPROVING beyond mark-reviewed: the bulk radio,
  //   and the note/receipt overrides the approve button reads. Primary + Approval;
  //   Group does not. SEE-1081: Secondary admins now SEE transactions and can mark
  //   them reviewed, but still do NOT approve — they stay excluded here so the
  //   "without impacting the approval process" guarantee holds.
  //   SEE-1296: the name is now half wrong — POSTING moved to
  //   canPostTransactionToErp. The role was built for flex, where there is nothing
  //   to post, so approve and post were never distinguished. Kept as-is rather than
  //   renamed across every call site; the approve half is what it still gates.
  //
  // canAccessAdminReimbursements — gates `/overview/reimbursements`
  //   (the admin-side reimbursements queue). Approval admins approve
  //   reimbursements as part of their final-approver scope per the matrix.
  //   Secondary admins see the queue and can export, but not approve/deny
  //   (that stays on canApproveDenyReimbursements). SEE-943: Group Admins
  //   join the queue too, restricted to the Requested/Reviewed tabs and to
  //   cardholders in their groups (scoped server-side).
  //
  // canAccessProfilePersonalTabs — gates the Profile-page personal tabs
  //   (my-transactions, my-cards, my-reimbursements). Primary admins use
  //   /profile for personal surfaces (their /overview is the admin dashboard);
  //   Approval admins follow the same pattern.
  //
  // canHoldOwnCards — defense-in-depth gate consumed by the card-assignment
  //   dropdown filter. Secondary admins are the only role denied here by the
  //   matrix; everyone else (including Group Admin and Approval Admin) can
  //   hold their own cards. Fail-open: when the flag is off, Secondary stored
  //   in DDB is already routed through isAdministrator, so this stays `true`
  //   for them — no regression for non-flag paidols.
  const canApproveAndPostErp = isAdministrator || isApprovalAdmin;
  // SEE-1296: POSTING ONLY — bulk Post and Bypass posting. An Approval Admin approves
  // but does not post, which is what the sidesheet's single-post gate already assumed.
  // Nothing about approving is restricted here; that stays on canApproveAndPostErp.
  const canPostTransactionToErp = isAdministrator;
  const canAccessAdminReimbursements = isAdministrator || isApprovalAdmin || isSecondaryAdmin || isGroupAdmin;
  // Approve/deny/reevaluate — excludes Secondary, whose only write is Export.
  const canApproveDenyReimbursements = isAdministrator || isApprovalAdmin;
  // SEE-943: reimbursements follow the transaction review/approval flow.
  // Mark-reviewed and coding mirror the transaction matrix minus Secondary
  // (SEE-1217 kept Secondary's only reimbursement write as Export). Group
  // Admins review/code only items from cardholders in their groups, and only
  // while REQUESTED — the per-status rules live in ReimbursementSheet and are
  // enforced server-side.
  const canMarkReimbursementReviewed = isAdministrator || isApprovalAdmin || isGroupAdmin;
  const canCodeReimbursements = isAdministrator || isApprovalAdmin || isGroupAdmin;
  // Export (and the APPROVED -> EXPORTED move) stays on the paidol-wide
  // admin roles; Group Admins never see the Approved/Exported tabs.
  const canExportReimbursements = isAdministrator || isApprovalAdmin || isSecondaryAdmin;
  // Submit a reimbursement request — every role with a personal My
  // Reimbursements surface on web (Primary/Approval via Profile, Group/
  // Cardholder via Overview). Secondary is the only role with no such surface.
  const canSubmitReimbursement = !isSecondaryAdmin;
  const canAccessProfilePersonalTabs = isAdministrator || isApprovalAdmin;
  const canHoldOwnCards = !isSecondaryAdmin;

  // Invite scope — Secondary cannot invite Primary; Approval/Cardholder cannot
  // invite anyone; Group Admin can invite Cardholders only (unchanged).
  const canInvitePrimaryAdmin = isAdministrator;
  // SEE-271: confirm a "Not signed up yet" member's sign-up. Primary admins and support users
  // (is_support, whatever their role in this company); the server enforces the same rule.
  const canVerifyMembers = isAdministrator || user?.is_support === true;
  // Organization → Audit log: the same pair, Primary admins and support users.
  const canViewAuditLog = canVerifyMembers;
  const canInviteSecondaryAdmin = isAdministrator || isSecondaryAdmin;
  const canInviteApprovalAdmin = isAdministrator || isSecondaryAdmin;
  const canInviteGroupAdmin = isAdministrator || isSecondaryAdmin;
  const canInviteCardholder = isAdministrator || isSecondaryAdmin || isGroupAdmin;
  const canInviteAnyone =
    canInvitePrimaryAdmin ||
    canInviteSecondaryAdmin ||
    canInviteApprovalAdmin ||
    canInviteGroupAdmin ||
    canInviteCardholder;

  const userHasAccessToCreditManagement = useMemo(() => {
    // Primary Admin only — Secondary cannot access credit management.
    return isAdministrator && integration?.highnoteProduct?.type === HighnoteProductType.CREDIT;
  }, [isAdministrator, integration?.highnoteProduct?.type]);

  // SEE-946: route a freshly-signed-in user (or one blocked by RoleRouteGuard)
  // to a page they can actually use. Reads straight from the stored role —
  // no derivation needed now that the capability checks above are pure.
  const roleAccess = getRoleAccess(userRole);

  return {
    // Raw roles
    isAdministrator,
    isSecondaryAdmin,
    isApprovalAdmin,
    isGroupAdmin,
    isCardholder,

    // Composite capabilities
    canManageUsersAndCards,
    canAccessSettings,
    canAccessTransactions,
    canMarkTransactionReviewed,
    canApproveTransactionFinal,
    canViewTxnReviewNotifications,
    canApproveAndPostErp,
    canPostTransactionToErp,
    canAccessAdminReimbursements,
    canApproveDenyReimbursements,
    canMarkReimbursementReviewed,
    canCodeReimbursements,
    canExportReimbursements,
    canSubmitReimbursement,
    canAccessProfilePersonalTabs,
    canHoldOwnCards,
    canInvitePrimaryAdmin,
    canInviteSecondaryAdmin,
    canInviteApprovalAdmin,
    canInviteGroupAdmin,
    canInviteCardholder,
    canInviteAnyone,
    canVerifyMembers,
    canViewAuditLog,

    // Routing — see app/auth/roleAccess.ts
    defaultLandingRoute: roleAccess.defaultLanding,
    showsHomeIcon: roleAccess.showsHomeIcon,

    // Credit
    userHasAccessToCreditManagement,

    // Feature-flag state (rarely needed externally, exposed for the invite form)
    additionalAdminRolesEnabled,
  };
};
